import { useQuery } from '@tanstack/react-query'
import { BadgeCheck, ShieldAlert, ShieldCheck } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  CardDescription,
  CardEyebrow,
  CardHeader,
  CardTitle,
  Note,
} from '../components/ui/card'
import { Button } from '../components/ui/button'
import { get } from '../lib/api'

/**
 * Public certificate verification — slide 12's "Verifiable anywhere".
 *
 * Reached by scanning the QR code printed on a Certificate of Participation.
 * Deliberately readable by anyone: the person checking a certificate is
 * usually an employer or the Registrar, who will have no account here.
 */

interface Verification {
  valid: true
  certNo: string
  name: string
  studentId: string | null
  school: string
  issuedAt: string
  boothsVisited: number
  totalBooths: number
  event: { name: string; tagline: string; dateLabel: string; organiser: string }
}

export default function VerifyCertificate() {
  const { token = '' } = useParams()

  const query = useQuery({
    queryKey: ['verify', token],
    // 'none': verification carries no session, by design.
    queryFn: () => get<Verification>(`/api/verify/${encodeURIComponent(token)}`, 'none'),
    enabled: token.length > 0,
    retry: false,
    staleTime: Infinity,
  })

  if (query.isLoading) {
    return (
      <Card>
        <CardContent>
          <p className="py-10 text-center text-[13px] text-muted">Checking this certificate…</p>
        </CardContent>
      </Card>
    )
  }

  if (query.isError || !query.data) {
    return (
      <Card>
        <CardContent className="text-center">
          <span className="mx-auto mb-3 grid size-14 place-items-center rounded-full bg-orange-soft text-orange">
            <ShieldAlert className="size-7" strokeWidth={2.2} />
          </span>

          <CardTitle>Certificate not recognised</CardTitle>
          <CardDescription className="mt-2">
            This link does not match any certificate issued for Lincoln International SDG &amp;
            Cultural Day 2026.
          </CardDescription>

          <Note tone="error" className="mt-4 text-left">
            Check that the whole address was scanned. If it was printed or forwarded by hand, a
            single missing character is enough to make it unreadable.
          </Note>

          <Button variant="outline" block className="mt-5" asChild>
            <Link to="/">Go to the event page</Link>
          </Button>
        </CardContent>
      </Card>
    )
  }

  const c = query.data

  return (
    <Card className="overflow-hidden p-0">
      {/* Green banner: the answer to "is this genuine?" comes before the detail. */}
      <div className="bg-linear-to-br from-green to-teal px-5 py-6 text-center text-white">
        <span className="mx-auto mb-2.5 grid size-14 place-items-center rounded-full bg-white/15 ring-1 ring-white/25">
          <ShieldCheck className="size-7" strokeWidth={2.2} />
        </span>
        <p className="text-[18px] font-bold leading-tight">Certificate verified</p>
        <p className="mt-1 text-[12px] text-white/80">
          Issued by {c.event.organiser.split(',')[0]}
        </p>
      </div>

      <CardContent className="pt-5">
        <CardEyebrow>Certificate of Participation</CardEyebrow>

        <p className="mt-2 text-[22px] font-bold leading-tight text-navy-950">{c.name}</p>
        <p className="mt-1 text-[13px] text-muted">
          {c.studentId ? `${c.studentId} · ` : null}
          {c.school}
        </p>

        <div className="mt-4 rounded-xl border border-line bg-surface px-4 py-3">
          <p className="text-[9.5px] font-bold uppercase tracking-[0.18em] text-muted-3">
            Certificate No.
          </p>
          <p className="font-mono text-[17px] font-bold tabular-nums text-navy-950">{c.certNo}</p>
        </div>

        <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-green-soft px-4 py-3 text-left">
          <BadgeCheck className="mt-px size-4 shrink-0 text-green" strokeWidth={2.2} />
          <p className="text-[12.5px] leading-snug text-navy-950">
            Attended <b>{c.boothsVisited}</b> of the {c.totalBooths} school booths of{' '}
            {c.event.name},{' '}
            <span className="whitespace-nowrap">“{c.event.tagline}”</span>, on{' '}
            {c.event.dateLabel.replace(/^\w+,\s*/, '')}.
          </p>
        </div>

        <dl className="mt-4 divide-y divide-hairline border-t border-hairline text-[12.5px]">
          <div className="flex justify-between gap-4 py-2">
            <dt className="text-muted">Issued</dt>
            <dd className="text-right font-bold text-navy-950">
              {new Date(c.issuedAt).toLocaleString([], {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </dd>
          </div>
          <div className="flex justify-between gap-4 py-2">
            <dt className="text-muted">Booths visited</dt>
            <dd className="text-right font-bold text-navy-950">
              {c.boothsVisited} of {c.totalBooths}
            </dd>
          </div>
        </dl>

        <p className="mt-4 text-[11px] leading-relaxed text-muted-3">
          This page is generated from the event's own records at the moment you open it. To query a
          certificate, contact {c.event.organiser}.
        </p>
      </CardContent>
    </Card>
  )
}
